import React from "react";
import "./HowItWorks.css";

const HowItWorks = () => {
  return (
    <div className="how-it-works-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <h1>How GoalFlex Works</h1>
          <p>
            Getting into your next soccer game has never been easier. Follow
            these simple steps to join the GoalFlex community and start playing.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="steps-section">
        <div className="container">
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">01</div>
              <div className="step-icon">📱</div>
              <h3>Download the App</h3>
              <p>
                Get GoalFlex from the App Store or Google Play. Create your
                profile in under 2 minutes with your basic info, position
                preferences, and location.
              </p>
              <div className="step-features">
                <span>✓ Quick setup</span>
                <span>✓ Position preferences</span>
                <span>✓ Location settings</span>
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">02</div>
              <div className="step-icon">🔍</div>
              <h3>Find Games</h3>
              <p>
                Browse games near you by location, time, and date. Find pickup
                games that match your schedule and start playing in your local
                soccer community.
              </p>
              <div className="step-features">
                <span>✓ Browse by location</span>
                <span>✓ Filter by date & time</span>
                <span>✓ Real-time availability</span>
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">03</div>
              <div className="step-icon">⚡</div>
              <h3>Book Instantly</h3>
              <p>
                Reserve your spot with one tap. Get instant confirmation, game
                details, and connect with other players joining the same game.
              </p>
              <div className="step-features">
                <span>✓ One-tap booking</span>
                <span>✓ Instant confirmation</span>
                <span>✓ Player chat (coming soon)</span>
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">04</div>
              <div className="step-icon">⚽</div>
              <h3>Play & Connect</h3>
              <p>
                Show up and play! Rate your experience, connect with new
                players, and build your soccer network. Track your games and
                improve your skills.
              </p>
              <div className="step-features">
                <span>✓ Player ratings</span>
                <span>✓ Game history</span>
                <span>✓ Social features</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Deep Dive */}
      <section className="features-deep-dive">
        <div className="container">
          <h2>What Makes GoalFlex Different</h2>
          <div className="features-grid">
            <div className="feature-detail">
              <div className="feature-icon">🎯</div>
              <h3>Easy Game Discovery</h3>
              <p>
                Browse available games near you by location and schedule. Find
                pickup games that fit your availability and start playing with
                the local soccer community.
              </p>
            </div>

            <div className="feature-detail">
              <div className="feature-icon">✅</div>
              <h3>Verified Players</h3>
              <p>
                All players go through our verification process. We use ratings,
                reviews, and behavioral analysis to maintain a high-quality
                community.
              </p>
            </div>

            <div className="feature-detail">
              <div className="feature-icon">🏟️</div>
              <h3>Premium Venues</h3>
              <p>
                We partner with the best fields and facilities in your area.
                Quality pitches, proper lighting, and professional referees when
                needed.
              </p>
            </div>

            <div className="feature-detail">
              <div className="feature-icon">📊</div>
              <h3>Performance Tracking</h3>
              <p>
                Track your games, view your stats, and see your improvement over
                time. Get insights on your playing patterns and skill
                development.
              </p>
            </div>

            <div className="feature-detail">
              <div className="feature-icon">💬</div>
              <h3>Community Features</h3>
              <p>
                Connect with players, organize regular games, and build lasting
                friendships both on and off the field. Group chat feature coming
                soon!
              </p>
            </div>

            <div className="feature-detail">
              <div className="feature-icon">🔔</div>
              <h3>Smart Notifications</h3>
              <p>
                Get notified about games that match your preferences,
                last-minute openings, and updates from your regular playing
                groups.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>How much does it cost to use GoalFlex?</h4>
              <p>
                GoalFlex is free to download and use. You only pay for the games
                you book, which covers field rental, equipment, and any
                additional services like referees.
              </p>
            </div>

            <div className="faq-item">
              <h4>What if I need to cancel a game?</h4>
              <p>
                You can cancel up to 24 hours before game time for a full refund
                to your account as game credit. Cancellations within 24 hours
                may forfeit your payment.
              </p>
            </div>

            <div className="faq-item">
              <h4>How do you ensure player safety?</h4>
              <p>
                All players are verified through our rating system. We have
                clear community guidelines, and our venues are selected for
                safety and quality standards.
              </p>
            </div>

            <div className="faq-item">
              <h4>Can I bring friends to games?</h4>
              <p>
                Absolutely! You can invite friends to join games or book
                multiple spots. We encourage groups to play together and build
                communities.
              </p>
            </div>

            <div className="faq-item">
              <h4>What if it rains?</h4>
              <p>
                Most of our games are played at indoor facilities, so weather
                isn't usually an issue. For outdoor games, if weather conditions
                are unsafe, games will be cancelled and you'll receive a full
                refund as game credit to your account.
              </p>
            </div>

            <div className="faq-item">
              <h4>Are games organized by skill level?</h4>
              <p>
                Currently, games are open to all skill levels. We focus on
                creating a fun, inclusive environment where players of all
                abilities can enjoy pickup soccer together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Start Playing?</h2>
          <p>
            Join thousands of players who've already discovered the future of
            recreational soccer. Download GoalFlex today and find your next
            game.
          </p>
          <div className="cta-buttons">
            <a
              href="https://apps.apple.com/us/app/goalflex/id6745612755"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary-large"
            >
              Download for iOS
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.goalflex.app&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary-large"
            >
              Download for Android
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
